import { MenuItem } from "@material-ui/core"
import Add from "@material-ui/icons/Add"
import ViewCompact from "@material-ui/icons/ViewCompact"
import { useMemo } from "react"
import type { ReactElement } from "react"

import { useTranslation } from "react-i18next"
import { useIsOwner, useIsSafeExamBrowser } from "../../../hooks"
import { ComparisonLayout } from "../../../models"
import type { Comparison } from "../../../redux"
import LayoutIcon from "../../atoms/layout-icon/layout-icon.component"
import { MoreOptionsBase } from "../more-options-base/more-options-base.component"
import type { MenuOption } from "../more-options-base/more-options-base.component"
export interface IComparisonMoreOptionsProps {
  comparison: Comparison
  layout: ComparisonLayout
  onSave: () => void
  onSaveAs: () => void
  onAddMaps: () => void
  onHandIn: () => void
  onShare: () => void
  onSetLayout: (layout: ComparisonLayout) => void
}

export function ComparisonMoreOptions(props: IComparisonMoreOptionsProps): ReactElement {
  const { comparison, layout, onSave, onSaveAs, onAddMaps, onHandIn, onShare, onSetLayout } = props

  const { t } = useTranslation("comparison")
  const isOwner = useIsOwner(comparison)
  const isSafeExamBrowser = useIsSafeExamBrowser()

  const showAddMaps: boolean = useMemo<boolean>(() => {
    return isOwner && comparison.projects.length < 4
  }, [isOwner, comparison])
  // biome-ignore lint/correctness/useExhaustiveDependencies: TODO in a later PR
  const showChangeLayout: boolean = useMemo<boolean>(() => {
    const amount: number = comparison.projects.length
    return amount > 1 && amount < 4 && !isSafeExamBrowser
  }, [comparison])
  // biome-ignore lint/correctness/useExhaustiveDependencies: TODO in a later PR
  const optionsToShow: MenuOption[] = useMemo<MenuOption[]>(() => {
    return [
      {
        isShown: showAddMaps,
        buttonParams: {
          icon: <Add />,
          text: t("optionsButton.addMap"),
          onClick: onAddMaps
        }
      },
      {
        isShown: showChangeLayout,
        buttonParams: {
          icon: <ViewCompact />,
          text: t("optionsButton.changeLayout"),
          nestedItems: <LayoutMenuItems />
        }
      }
    ]
  }, [showAddMaps, showChangeLayout, layout])

  function LayoutMenuItems(): ReactElement {
    switch (comparison.projects.length) {
      case 2:
        return (
          <>
            <MenuItem onClick={() => onSetLayout(ComparisonLayout.Vertical)}>
              <LayoutIcon layout={ComparisonLayout.Vertical} selected={layout === ComparisonLayout.Vertical} />
            </MenuItem>
            <MenuItem onClick={() => onSetLayout(ComparisonLayout.Horizontal)}>
              <LayoutIcon layout={ComparisonLayout.Horizontal} selected={layout === ComparisonLayout.Horizontal} />
            </MenuItem>
          </>
        )
      case 3:
        return (
          <>
            <MenuItem onClick={() => onSetLayout(ComparisonLayout.HorizontalBottomSplit)}>
              <LayoutIcon
                layout={ComparisonLayout.HorizontalBottomSplit}
                selected={layout === ComparisonLayout.HorizontalBottomSplit}
              />
            </MenuItem>
            <MenuItem onClick={() => onSetLayout(ComparisonLayout.HorizontalTopSplit)}>
              <LayoutIcon
                layout={ComparisonLayout.HorizontalTopSplit}
                selected={layout === ComparisonLayout.HorizontalTopSplit}
              />
            </MenuItem>
            <MenuItem onClick={() => onSetLayout(ComparisonLayout.VerticalLeftSplit)}>
              <LayoutIcon
                layout={ComparisonLayout.VerticalLeftSplit}
                selected={layout === ComparisonLayout.VerticalLeftSplit}
              />
            </MenuItem>
            <MenuItem onClick={() => onSetLayout(ComparisonLayout.VerticalRightSplit)}>
              <LayoutIcon
                layout={ComparisonLayout.VerticalRightSplit}
                selected={layout === ComparisonLayout.VerticalRightSplit}
              />
            </MenuItem>
            <MenuItem onClick={() => onSetLayout(ComparisonLayout.VerticalThreeWaySplit)}>
              <LayoutIcon
                layout={ComparisonLayout.VerticalThreeWaySplit}
                selected={layout === ComparisonLayout.VerticalThreeWaySplit}
              />
            </MenuItem>
          </>
        )
      default:
        return <></>
    }
  }

  return (
    <MoreOptionsBase
      data={comparison}
      objectDescriptor="comparison"
      options={optionsToShow}
      onHandIn={onHandIn}
      onSave={onSave}
      onSaveAs={onSaveAs}
      onShare={onShare}
    />
  )
}
